import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RiArrowLeftLine } from 'react-icons/ri';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ComponnentsUI/common/Button';

type PatientFormPageShellProps = {
  title: string;
  subtitle: string;
  cancelLabel: string;
  submitLabel: string;
  savingLabel: string;
  returnTo: string;
  isSubmitting: boolean;
  isLoading?: boolean;
  onSubmit: () => void;
  children: React.ReactNode;
};

export const PatientFormPageShell: React.FC<PatientFormPageShellProps> = ({
  title,
  subtitle,
  cancelLabel,
  submitLabel,
  savingLabel,
  returnTo,
  isSubmitting,
  isLoading = false,
  onSubmit,
  children,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(returnTo);
  };

  return (
    <div className="bg-card-color-200 dark:bg-gray-900 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <button
              type="button"
              onClick={handleBack}
              disabled={isSubmitting}
              className="mt-1 rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-white disabled:opacity-50 dark:border-gray-700 dark:hover:bg-gray-800"
              aria-label={cancelLabel}
            >
              <RiArrowLeftLine className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-title-page-color-100 dark:text-white">
                {title}
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {subtitle}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:justify-end">
            <Button
              variant="secondary"
              onClick={handleBack}
              disabled={isSubmitting}
            >
              {cancelLabel}
            </Button>
            <Button onClick={onSubmit} disabled={isSubmitting || isLoading}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isSubmitting ? savingLabel : submitLabel}
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-card-color-100 shadow-sm dark:border-slate-700 dark:bg-gray-800">
          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-primary-dui" />
            </div>
          ) : (
            <div className="p-6">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
